import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, RefreshCw, Loader2, CheckCircle2, AlertTriangle, ExternalLink, Link2, Unplug, Sheet } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function fmt(iso) {
  if (!iso) return 'Never';
  try { return new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

export default function GoogleSheetsDialog({ open, onClose, token }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const auth = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/integrations/google-sheets/status`, { headers: auth });
      if (r.ok) setStatus(await r.json());
    } catch (_) { /* noop */ } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Reflect the OAuth callback result (?gsheet=connected|error) once.
  useEffect(() => {
    if (!open) return;
    const params = new URLSearchParams(window.location.search);
    const g = params.get('gsheet');
    if (g === 'connected') { toast.success('Google Sheet connected — importing eligible contacts…'); load(); }
    else if (g === 'error') { toast.error('Could not connect the Google Sheet. Please try again.'); }
    if (g) { params.delete('gsheet'); const q = params.toString(); window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : '')); }
  }, [open, load]);

  const connect = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/integrations/google-sheets/connect`, { headers: auth });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Could not start Google sign-in');
      window.location.assign(d.auth_url);
    } catch (e) { toast.error(e.message); setLoading(false); }
  };

  const connectPublic = async () => {
    setSyncing(true);
    try {
      const r = await fetch(`${API}/api/integrations/google-sheets/connect-public`, { method: 'POST', headers: auth });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Could not connect the sheet');
      const s = d.stats || {};
      toast.success(`Connected — imported ${s.imported || 0}, skipped ${s.skipped_booked || 0} booked-in, ${s.skipped_duplicate || 0} duplicates.`);
      load();
    } catch (e) { toast.error(e.message); } finally { setSyncing(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await fetch(`${API}/api/integrations/google-sheets/sync`, { method: 'POST', headers: auth });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Sync failed');
      const s = d.stats || {};
      toast.success(`Sync complete — ${s.imported || 0} imported, ${s.skipped_booked || 0} booked-in skipped, ${s.skipped_duplicate || 0} duplicates.`);
      load();
    } catch (e) { toast.error(e.message); } finally { setSyncing(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect the Google Sheet? Existing imported leads are kept; automatic syncing will stop.')) return;
    setLoading(true);
    try {
      await fetch(`${API}/api/integrations/google-sheets/disconnect`, { method: 'POST', headers: auth });
      toast.success('Disconnected.');
      load();
    } catch (e) { toast.error('Could not disconnect'); } finally { setLoading(false); }
  };

  if (!open) return null;
  const connected = status?.connected;
  const stats = status?.last_sync_stats;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="gsheet-dialog">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-3xl bg-white shadow-2xl ring-1 ring-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl bg-emerald-50 ring-1 ring-emerald-200 flex items-center justify-center">
              <Sheet className="w-[18px] h-[18px] text-emerald-600" />
            </span>
            <div>
              <h2 className="text-base font-black text-slate-900 leading-tight">Google Sheet import</h2>
              <p className="text-[11px] text-slate-500">Auto-imports new contacts into Lead Nurture</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="gsheet-close" className="text-slate-400 hover:text-slate-700 transition-colors"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {loading && !status ? (
            <div className="flex items-center justify-center py-8 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (
            <>
              {/* Connection status */}
              <div className={`rounded-2xl px-4 py-3 flex items-center gap-3 ${connected ? 'bg-emerald-50 ring-1 ring-emerald-200' : 'bg-slate-50 ring-1 ring-slate-200'}`} data-testid="gsheet-status">
                {connected
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                  : <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />}
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-900">{connected ? 'Connected & syncing' : 'Not connected'}</p>
                  <p className="text-[11px] text-slate-500 truncate">
                    {connected
                      ? `Last sync: ${fmt(status?.last_sync_at)} · ${status?.last_sync_status || '—'}`
                      : (status?.auth_configured ? 'Sign in with Google to connect the sheet.' : 'Google OAuth is not configured yet.')}
                  </p>
                </div>
              </div>

              {!status?.auth_configured && !connected && (
                <div className="rounded-xl bg-sky-50 ring-1 ring-sky-200 px-3 py-2.5 text-[12px] text-sky-800" data-testid="gsheet-not-configured">
                  This sheet is shared publicly, so you can connect it directly below — no Google sign-in needed. (For a private sheet, add <code className="font-mono">GOOGLE_CLIENT_ID</code>/<code className="font-mono">GOOGLE_CLIENT_SECRET</code> to enable Google sign-in.)
                </div>
              )}

              {status?.last_error && (
                <div className="rounded-xl bg-rose-50 ring-1 ring-rose-200 px-3 py-2 text-[12px] text-rose-700" data-testid="gsheet-error">{status.last_error}</div>
              )}

              {/* Last sync stats */}
              {connected && stats && (
                <div className="grid grid-cols-3 gap-2 text-center" data-testid="gsheet-stats">
                  {[['Imported', stats.imported, 'text-emerald-600'], ['Booked-in skipped', stats.skipped_booked, 'text-amber-600'], ['Duplicates', stats.skipped_duplicate, 'text-slate-500']].map(([label, val, tone]) => (
                    <div key={label} className="rounded-xl bg-slate-50 ring-1 ring-slate-200 py-2.5">
                      <p className={`text-xl font-black tabular-nums ${tone}`}>{val ?? 0}</p>
                      <p className="text-[10px] font-semibold text-slate-500 leading-tight mt-0.5">{label}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Sheet link */}
              {status?.sheet_url && (
                <a href={status.sheet_url} target="_blank" rel="noreferrer" data-testid="gsheet-open-link"
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-700 hover:underline">
                  <ExternalLink className="w-3.5 h-3.5" /> Open the live Google Sheet
                </a>
              )}

              <div className="rounded-xl bg-slate-50 ring-1 ring-slate-100 px-3 py-2.5 text-[11px] text-slate-500 leading-relaxed">
                {status?.min_submitted_at && (
                  <p className="mb-1" data-testid="gsheet-window">
                    Importing contacts submitted on/after <span className="font-bold text-slate-700">{fmt(status.min_submitted_at)}</span> (this week onwards).
                  </p>
                )}
                Contacts whose Notes contain <span className="font-bold text-slate-700">“BOOKED IN!”</span> are never imported. Duplicates (matching mobile or email) are skipped — existing leads are never overwritten.
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-1">
                {connected ? (
                  <>
                    <button onClick={syncNow} disabled={syncing} data-testid="gsheet-sync-now"
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[#0f172a] text-white text-sm font-bold px-4 py-2.5 hover:bg-slate-800 disabled:opacity-60 transition-colors">
                      {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sync now
                    </button>
                    <button onClick={disconnect} data-testid="gsheet-disconnect"
                      className="inline-flex items-center justify-center gap-1.5 rounded-xl ring-1 ring-slate-200 text-slate-600 text-sm font-bold px-4 py-2.5 hover:bg-slate-50 transition-colors">
                      <Unplug className="w-4 h-4" /> Disconnect
                    </button>
                  </>
                ) : (
                  <div className="flex-1 flex flex-col gap-2">
                    <button onClick={connectPublic} disabled={syncing} data-testid="gsheet-connect-public"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 text-white text-sm font-bold px-4 py-2.5 hover:bg-emerald-700 disabled:opacity-60 transition-colors">
                      {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Connect &amp; import now
                    </button>
                    {status?.auth_configured && (
                      <button onClick={connect} disabled={loading} data-testid="gsheet-connect"
                        className="w-full inline-flex items-center justify-center gap-2 rounded-xl ring-1 ring-slate-200 text-slate-700 text-sm font-bold px-4 py-2.5 hover:bg-slate-50 disabled:opacity-50 transition-colors">
                        Sign in with Google instead
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
